import type { AgentMetadataHelpers } from '../api/agent-metadata.api';

/**
 * Singleton binding for the agent-metadata helpers.
 *
 * The composition root (`lib/config/agents.config.ts`) calls
 * `configureAgentMetadata()` once at module load, providing helpers
 * pre-bound to its concrete `agentMetadata` map. Consumers (e.g.
 * `features/chats`) then call `getAgentMetadataHelpers()` to access
 * these bound helpers WITHOUT importing `lib/config/agents.config`.
 *
 * This breaks the cyclic dependency that previously formed between
 * `agents.config.ts` (which imports widget components from features) and
 * `features/chats` (which needs lookup helpers operating on agent
 * metadata).
 *
 * Pattern mirrors `entities/widget/lib/widget-store-bindings.ts` and
 * `features/chats/lib/chat-store-bindings.ts`.
 */

let bindings: AgentMetadataHelpers | null = null;

/**
 * Configures the agent-metadata helpers. Called once at app boot from
 * the composition root. Safe to call multiple times — subsequent calls
 * replace the bindings (used by tests via `configureAgentMetadataForTests`).
 */
export const configureAgentMetadata = (helpers: AgentMetadataHelpers): void => {
  bindings = helpers;
};

/**
 * Returns the bound agent-metadata helpers. Throws fail-fast if called
 * before `configureAgentMetadata()` runs at app boot.
 */
export const getAgentMetadataHelpers = (): AgentMetadataHelpers => {
  if (bindings === null) {
    throw new Error(
      '[entities/agent] Agent metadata bindings not configured. ' +
        'Call `configureAgentMetadata` at app boot before any consumer uses ' +
        'agent-metadata helpers.',
    );
  }
  return bindings;
};

/**
 * Test-only reset. Re-applies the binding from a fresh helpers reference,
 * letting tests swap bound implementations between runs. Production code
 * MUST NOT call this — use `configureAgentMetadata` once at boot.
 */
export const configureAgentMetadataForTests = (helpers: AgentMetadataHelpers | null): void => {
  bindings = helpers;
};
